import React from 'react';
import { Typography, Flex } from 'antd';
import { CheckCircleOutlined, CloseCircleFilled } from '@ant-design/icons';
import './ProgressSteps.css';

export type ProgressStatus = 'normal' | 'poor' | 'success';
export type ProgressSize = 'default' | 'small' | 'large';

export interface ProgressStepsProps {

  percent?: number;
  status?: ProgressStatus;
  showInfo?: boolean;
  size?: ProgressSize;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * ProgressSteps component - A segmented progress bar with different status variants
 * Based on Figma design specifications
 */
export const ProgressSteps: React.FC<ProgressStepsProps> = ({
  percent = 0,
  status = 'normal',
  showInfo = true,
  size = 'default',
  style,
  className,
}) => {
  // Calculate number of filled segments (4 total segments)
  const filledSteps = Math.round((percent / 100) * 4);

  // Determine color based on status
  const getColor = (): string => {
    switch (status) {
      case 'success':
        return '#52c41a'; // Green
      case 'poor':
        return '#ff4d4f'; // Red
      case 'normal':
      default:
        // Lime/yellow for normal status (Figma: #e3f571)
        return '#e3f571';
    }
  };

  // Determine whether to show icon instead of percentage
  const shouldShowIcon = status === 'success' || status === 'poor' || percent === 100 || percent === 0;

  // Get bar height based on size
  const getBarHeight = (): string => {
    switch (size) {
      case 'small':
        return '6px';
      case 'large':
        return '10px';
      case 'default':
      default:
        return '8px';
    }
  };

  const color = getColor();
  const barHeight = getBarHeight();

  return (
    <Flex
      align="center"
      gap={8}
      style={style}
      className={`progress-steps ${className || ''}`}
    >
      {/* Progress Bar Container */}
      <div
        className="progress-steps-bar"
        style={{
          display: 'flex',
          gap: '2px',
          alignItems: 'flex-start',
          flex: 1,
          height: barHeight,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* 4 Segments */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`progress-steps-segment ${i < filledSteps ? 'filled' : 'empty'}`}
            style={{
              flex: '1 0 0',
              backgroundColor: i < filledSteps ? color : 'rgba(0, 0, 0, 0.06)',
              height: '100%',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* Info Display (Icon or Percentage) */}
      {showInfo && (
        <>
          {shouldShowIcon ? (
            <>
              {status === 'success' || percent === 100 ? (
                <CheckCircleOutlined
                  className="progress-steps-icon success"
                  style={{
                    color: '#52c41a',
                    fontSize: size === 'small' ? '10px' : size === 'large' ? '14px' : '12px',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <CloseCircleFilled
                  className="progress-steps-icon exceptional"
                  style={{
                    color: '#ff4d4f',
                    fontSize: size === 'small' ? '10px' : size === 'large' ? '14px' : '12px',
                    flexShrink: 0,
                  }}
                />
              )}
            </>
          ) : (
            <Typography.Text
              className="progress-steps-percentage"
              style={{
                flexShrink: 0,
                fontSize: size === 'small' ? '10px' : size === 'large' ? '14px' : '12px',
                lineHeight: '20px',
                color: 'rgba(33, 33, 47, 0.88)',
                fontWeight: 400,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              {percent}%
            </Typography.Text>
          )}
        </>
      )}
    </Flex>
  );
};

export default ProgressSteps;
